import { createTheme } from '@mui/material/styles';

// Light and dark palettes side by side so both stay visually consistent —
// same brand hues (primary blue, navy secondary), just re-balanced for
// each background. `light`/`dark` sub-shades are used by MUI itself for
// things like Alert/Chip "light" variants and hover/active states, so they
// need real per-mode values too, not just the `main` swapped.
const PALETTES = {
  light: {
    mode: 'light',
    primary: { main: '#0D629B', light: '#1a7abf', dark: '#0a4f7d', contrastText: '#ffffff' },
    secondary: { main: '#001A3C', light: '#002a5c', dark: '#001028', contrastText: '#ffffff' },
    success: { main: '#0E9F6E', light: '#ECFDF5', dark: '#047857' },
    warning: { main: '#C05621', light: '#FFFBEB', dark: '#9C4221' },
    error: { main: '#C53030', light: '#FFF5F5', dark: '#9B2C2C' },
    info: { main: '#2B6CB0', light: '#EBF4FF' },
    background: { default: '#F4F6FA', paper: '#FFFFFF' },
    text: { primary: '#0E1420', secondary: '#4A5568', disabled: '#8A96A8' },
    divider: '#E5E9F0',
    action: { hover: '#F9FAFB', selected: '#EEF1F6' },
    // Dedicated hover-border shade, distinct from text.disabled — restores
    // the original theme's exact value instead of reusing a text token
    // that happened to be darker and visibly changed TextField's hover
    // state in light mode.
    inputHoverBorder: '#D0D7E3',
  },
  dark: {
    mode: 'dark',
    // Same blue, brightened slightly so it keeps enough contrast against a
    // near-black background instead of reading as muddy.
    primary: { main: '#3B93CE', light: '#67B0DE', dark: '#0D629B', contrastText: '#06111D' },
    // The brand navy (#001A3C) is almost black — unusable as an accent
    // color on a dark background, so dark mode uses a lightened version of
    // the same hue instead of the literal brand value.
    secondary: { main: '#6E8FB8', light: '#93ABC9', dark: '#4A6482', contrastText: '#06111D' },
    success: { main: '#34D399', light: '#0B3B2E', dark: '#047857' },
    warning: { main: '#F6AD55', light: '#4A2E12', dark: '#9C4221' },
    error: { main: '#FC8181', light: '#4A1C1C', dark: '#9B2C2C' },
    info: { main: '#63B3ED', light: '#17304D' },
    background: { default: '#0B1220', paper: '#141B2D' },
    text: { primary: '#F4F6FA', secondary: '#A9B4C4', disabled: '#5B6B80' },
    divider: 'rgba(255,255,255,0.12)',
    action: { hover: 'rgba(255,255,255,0.06)', selected: 'rgba(255,255,255,0.1)' },
    inputHoverBorder: 'rgba(255,255,255,0.3)',
  },
};

// The admin/customer sidebar and the AuthLayout's left branding panel are
// deliberately always this dark navy, independent of light/dark mode —
// it's brand chrome, not content, same idea as a logo never "going light
// mode". Exported so layouts that hardcode it stay in one place.
export const BRAND_SIDEBAR_BG = '#001A3C';

export const getTheme = (mode = 'light') => {
  const palette = PALETTES[mode] || PALETTES.light;

  return createTheme({
    palette,

    typography: {
      fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 },
      h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 },
      h3: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4 },
      h4: { fontSize: '1.125rem', fontWeight: 700 },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem', lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5, color: palette.text.secondary },
      caption: { fontSize: '0.75rem', color: palette.text.disabled },
      overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
    },

    shape: {
      borderRadius: 10,
    },

    shadows:
      mode === 'dark'
        ? [
            'none',
            '0 1px 2px rgba(0,0,0,0.3)',
            '0 1px 3px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.25)',
            '0 4px 16px rgba(0,0,0,0.4)',
            '0 8px 24px rgba(0,0,0,0.45)',
            ...Array(20).fill('0 8px 32px rgba(0,0,0,0.5)'),
          ]
        : [
            'none',
            '0 1px 2px rgba(0,0,0,0.05)',
            '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
            '0 4px 16px rgba(0,0,0,0.08)',
            '0 8px 24px rgba(0,0,0,0.1)',
            ...Array(20).fill('0 8px 32px rgba(0,0,0,0.12)'),
          ],

    components: {
      // NOT a function here, unlike the other overrides below — MUI's
      // CssBaseline (see node_modules/@mui/material/CssBaseline/CssBaseline.js)
      // does `defaultStyles = [defaultStyles, themeOverrides]` for this one
      // specific slot instead of calling it as `styleOverrides(theme)` the
      // way every other component's root/slot overrides are invoked. A
      // function here gets pushed in unevaluated, so it silently never
      // applies (this was the actual cause of "light mode background is
      // still dark" — theme.components.MuiCssBaseline wasn't doing
      // anything at all, not that it was picking the wrong mode). No body
      // background/color override is needed anyway — CssBaseline already
      // applies `theme.palette.background.default` / `text.primary` to
      // body on its own; this block only needs to add the extra bits
      // CssBaseline doesn't already do (the font import, scrollbar). The
      // scrollbar thumb color comes from global.css's CSS variable (kept
      // in sync with mode via the `data-theme-mode` attribute) instead of
      // a theme value, precisely so this can stay a plain static object.
      MuiCssBaseline: {
        styleOverrides: {
          '@import': "url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap')",
          '*::-webkit-scrollbar': { width: 4, height: 4 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': { background: 'var(--app-scrollbar-thumb)', borderRadius: 99 },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
          sizeMedium: { padding: '8px 18px' },
          sizeLarge: { padding: '10px 24px' },
          containedPrimary: ({ theme: t }) => ({
            background: t.palette.primary.main,
            '&:hover': { background: t.palette.primary.light },
          }),
        },
      },

      MuiCard: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: 12,
            border: `1px solid ${t.palette.divider}`,
            boxShadow: t.shadows[2],
            backgroundImage: 'none',
          }),
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundImage: 'none',
            border: `1px solid ${t.palette.divider}`,
          }),
          elevation0: { border: 'none' },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            '& .MuiTableCell-head': {
              backgroundColor: t.palette.action.hover,
              color: t.palette.text.disabled,
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: `1px solid ${t.palette.divider}`,
            },
          }),
        },
      },

      MuiTableBody: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            '& .MuiTableRow-root': {
              '&:hover': { backgroundColor: t.palette.action.hover },
              cursor: 'pointer',
            },
            '& .MuiTableCell-body': {
              fontSize: '0.8125rem',
              borderBottom: `1px solid ${t.palette.divider}`,
              padding: '13px 16px',
            },
          }),
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: '0.6875rem',
            height: 22,
            borderRadius: 99,
          },
        },
      },

      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
        styleOverrides: {
          root: ({ theme: t }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '& fieldset': { borderColor: t.palette.divider },
              '&:hover fieldset': { borderColor: t.palette.inputHoverBorder },
            },
          }),
        },
      },

      // Sidebar/top-bar nav surfaces deliberately stay brand navy in both
      // modes (see BRAND_SIDEBAR_BG above) — these two overrides are the
      // same in light and dark on purpose, not an oversight.
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            background: BRAND_SIDEBAR_BG,
            border: 'none',
            boxShadow: t.palette.mode === 'dark' ? '4px 0 24px rgba(0,0,0,0.4)' : '4px 0 24px rgba(0,0,0,0.15)',
          }),
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: t.palette.background.paper,
            color: t.palette.text.primary,
            boxShadow: 'none',
            borderBottom: `1px solid ${t.palette.divider}`,
          }),
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            padding: '9px 18px',
            '&.Mui-selected': {
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: 3,
                background: '#0D629B',
                borderRadius: '0 2px 2px 0',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.07)',
            },
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 99, height: 5 },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 16 },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10, fontSize: '0.8125rem' },
        },
      },
    },
  });
};

// Default export kept as a plain light theme for any import that hasn't
// been migrated to ColorModeContext's `useColorMode()` yet (see
// src/contexts/ColorModeContext.jsx) — avoids a hard break mid-migration.
const theme = getTheme('light');
export default theme;
